import type { ReactNode } from 'react';

interface Column<T> {
  key: string;
  header: string;
  render?: (row: T, index: number) => ReactNode;
  sortable?: boolean;
  sortKey?: string;
}

interface Pagination {
  hasPrevious?: boolean;
  hasNext?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  isLoading?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T, index: number) => string;
  emptyMessage?: string;
  loading?: boolean;
  isFetching?: boolean;
  pagination?: Pagination;
  countLabel?: string;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  onRowClick?: (row: T, index: number) => void;
  sort?: string;
  order?: 'asc' | 'desc';
  onSort?: (sort: string, order: 'asc' | 'desc') => void;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const SKELETON_ROWS = 10;

function getNextOrder(
  columnSortKey: string,
  currentSort?: string,
  currentOrder?: 'asc' | 'desc'
): 'asc' | 'desc' {
  if (currentSort !== columnSortKey) return 'asc';
  if (currentOrder === 'asc') return 'desc';
  return 'asc';
}

function SkeletonCell() {
  return (
    <div className="h-4 w-3/4 rounded bg-[var(--border)]/60 animate-pulse" />
  );
}

function SortIndicator({
  active,
  order,
}: {
  active: boolean;
  order?: 'asc' | 'desc';
}) {
  return (
    <span
      className={`ml-1 text-xs transition ${
        active ? 'text-[var(--primary)]' : 'text-[var(--muted)]/50'
      }`}
      aria-hidden="true"
    >
      {active ? (order === 'asc' ? '▲' : '▼') : '▲'}
    </span>
  );
}

export default function DataTable<T>({
  columns,
  data,
  keyExtractor,
  emptyMessage = 'No records found.',
  loading = false,
  isFetching = false,
  pagination,
  countLabel,
  pageSize,
  onPageSizeChange,
  onRowClick,
  sort,
  order,
  onSort,
}: DataTableProps<T>) {
  const showPagination = Boolean(
    pagination || data.length > 0 || pageSize !== undefined
  );
  const showViewColumn = Boolean(onRowClick);

  const isInitialLoading = loading && data.length === 0;
  const showFetchingOverlay = isFetching && data.length > 0;

  if (!loading && data.length === 0) {
    return (
      <div className="rounded-xl border border-[var(--border)] bg-[var(--card)]/60 p-12 text-center text-[var(--muted)]">
        {emptyMessage}
      </div>
    );
  }

  const rowClass =
    'h-11 border-b border-[var(--border)]/60 text-sm text-[var(--fg)] transition last:border-0 hover:bg-[var(--bg)]/60';

  return (
    <div className="dashboard-card relative overflow-hidden p-0">
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse text-left">
          <thead className="sticky top-0 z-10">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="h-11 border-b border-[var(--border)] bg-[var(--card)] px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-[var(--muted)]"
                >
                  {col.sortable && col.sortKey ? (
                    <button
                      type="button"
                      onClick={() => onSort?.(col.sortKey!, getNextOrder(col.sortKey!, sort, order))}
                      className="flex items-center transition hover:text-[var(--fg)]"
                      aria-sort={
                        sort === col.sortKey
                          ? order === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      {col.header}
                      <SortIndicator
                        active={sort === col.sortKey}
                        order={order}
                      />
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
              {showViewColumn && (
                <th className="h-11 w-20 border-b border-[var(--border)] bg-[var(--card)] px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-[var(--muted)]">
                  View
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {isInitialLoading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
                <tr key={`skeleton-${rowIdx}`} className={rowClass}>
                  {columns.map((col) => (
                    <td key={col.key} className="px-4 py-2.5">
                      <SkeletonCell />
                    </td>
                  ))}
                  {showViewColumn && (
                    <td className="px-4 py-2.5">
                      <SkeletonCell />
                    </td>
                  )}
                </tr>
              ))
            ) : (
              data.map((row, rowIndex) => (
                <tr
                  key={keyExtractor(row, rowIndex)}
                  onClick={() => onRowClick?.(row, rowIndex)}
                  className={rowClass + ' cursor-pointer'}
                >
                  {columns.map((col) => {
                    const content = col.render
                      ? col.render(row, rowIndex)
                      : String(
                          (row as Record<string, unknown>)[col.key] ?? '—'
                        );
                    return (
                      <td
                        key={col.key}
                        className="truncate px-4 py-2.5"
                        title={typeof content === 'string' ? content : undefined}
                      >
                        {content}
                      </td>
                    );
                  })}
                  {showViewColumn && (
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick?.(row, rowIndex);
                        }}
                        className="rounded-md border border-[var(--border)] bg-[var(--bg)] px-2.5 py-1 text-xs font-medium text-[var(--fg)] transition hover:border-[var(--primary)] hover:text-[var(--primary)]"
                      >
                        View
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {showFetchingOverlay && (
          <div className="pointer-events-none absolute inset-0 bg-[var(--card)]/40 backdrop-blur-[1px] transition-opacity" />
        )}
      </div>

      {showPagination && (
        <div className="flex flex-col items-center justify-between gap-3 border-t border-[var(--border)] px-4 py-3 text-sm text-[var(--muted)] sm:flex-row">
          <span>{countLabel ?? `${data.length.toLocaleString()} records`}</span>

          <div className="flex items-center gap-3">
            {onPageSizeChange && pageSize !== undefined && (
              <label className="flex items-center gap-2">
                <span className="text-xs">Rows per page</span>
                <select
                  value={pageSize}
                  onChange={(e) =>
                    onPageSizeChange(Number(e.target.value))
                  }
                  disabled={loading || isFetching}
                  className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-2 py-1.5 text-sm text-[var(--fg)] outline-none ring-[var(--ring)] transition focus:border-[var(--primary)] focus:ring-1 disabled:opacity-60"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {pagination && (
              <div className="flex items-center gap-2">
                <button
                  onClick={pagination.onPrevious}
                  disabled={!pagination.hasPrevious || pagination.isLoading}
                  className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-1.5 text-[var(--fg)] transition hover:border-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={pagination.onNext}
                  disabled={!pagination.hasNext || pagination.isLoading}
                  className="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-1.5 text-[var(--fg)] transition hover:border-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
